'use client';

/**
 * AdminPendientesProvider (R50) — shared source of truth for the admin
 * "pending work" summary. Fetches GET /admin/pendientes/resumen once the
 * session is a known admin and re-fetches on navigation + window focus, so
 * the sidebar total badge and the /admin module badges stay fresh (e.g. after
 * an admin resolves a solicitud/alerta and navigates back).
 *
 * Non-admins get a null summary (no fetch, no badges). Mounted above the app
 * shell so both the sidebar and the /admin landing can read the same data.
 */

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { getPendientesResumen } from '@/lib/pendientes';
import type { PendientesResumen } from '@siiv/schemas';

interface AdminPendientesContextValue {
	/** Latest summary, or null when unknown / caller is not admin. */
	resumen: PendientesResumen | null;
	/** Force a re-fetch (e.g. right after resolving a solicitud/alerta). */
	refresh: () => void;
}

const AdminPendientesContext = createContext<AdminPendientesContextValue>({
	resumen: null,
	refresh: () => {},
});

export function AdminPendientesProvider({ children }: { children: ReactNode }) {
	const { user, status } = useAuthSession();
	const pathname = usePathname();
	const [resumen, setResumen] = useState<PendientesResumen | null>(null);

	const isAdmin = status === 'authenticated' && user?.role === 'admin';

	// Guard against overlapping fetches and stale writes across renders.
	const inFlightRef = useRef(false);

	const refresh = useCallback(async () => {
		if (!isAdmin || inFlightRef.current) return;
		inFlightRef.current = true;
		try {
			setResumen(await getPendientesResumen());
		} catch {
			// Best-effort: a failed count must never break the shell. Keep the
			// previous value (or null) and let the next trigger retry.
		} finally {
			inFlightRef.current = false;
		}
	}, [isAdmin]);

	// Drop stale data as soon as the session stops being an admin.
	useEffect(() => {
		if (!isAdmin) {
			setResumen(null);
			return;
		}
		void refresh();
	}, [isAdmin, refresh, pathname]);

	// Refresh when the tab regains focus (covers long-lived admin sessions).
	useEffect(() => {
		if (!isAdmin) return;
		const onFocus = () => void refresh();
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, [isAdmin, refresh]);

	return (
		<AdminPendientesContext.Provider value={{ resumen, refresh: () => void refresh() }}>
			{children}
		</AdminPendientesContext.Provider>
	);
}

export function useAdminPendientes() {
	return useContext(AdminPendientesContext);
}
